/**
 * Resource Counting System - Phase 6b Implementation
 * Real-time resource counting for accurate header displays
 */

import { useState, useEffect } from 'react';
import type { CloudProvider } from '@/types/cloudProvider';

// Mock resource data structure for counting
interface ResourceCount {
  total: number;
  running: number;
  stopped: number;
  error: number;
}

type ResourceCounts = Record<CloudProvider, ResourceCount>;

// Mock resource counting service
const mockResourceCounts: ResourceCounts = {
  aws: { total: 23, running: 18, stopped: 3, error: 2 },
  gcp: { total: 15, running: 12, stopped: 2, error: 1 },
  azure: { total: 9, running: 7, stopped: 1, error: 1 }
};

/**
 * Hook for managing dynamic resource counts
 * Simulates real-time updates and provider-specific counting
 */
export const useProviderResourceCounts = () => {
  const [counts, setCounts] = useState<Record<string, number>>({
    aws: mockResourceCounts.aws.total,
    gcp: mockResourceCounts.gcp.total,
    azure: mockResourceCounts.azure.total,
    all: mockResourceCounts.aws.total + mockResourceCounts.gcp.total + mockResourceCounts.azure.total
  });

  const [isLoading, setIsLoading] = useState(false);

  // Simulate real-time resource counting updates
  useEffect(() => {
    const updateCounts = () => {
      setIsLoading(true);
      
      // Simulate API delay
      setTimeout(() => {
        const awsCount = mockResourceCounts.aws.total + Math.floor(Math.random() * 5) - 2;
        const gcpCount = mockResourceCounts.gcp.total + Math.floor(Math.random() * 3) - 1;
        const azureCount = mockResourceCounts.azure.total + Math.floor(Math.random() * 3) - 1;
        
        const newCounts = {
          aws: Math.max(0, awsCount),
          gcp: Math.max(0, gcpCount),
          azure: Math.max(0, azureCount),
          all: Math.max(0, awsCount) + Math.max(0, gcpCount) + Math.max(0, azureCount)
        };
        
        setCounts(newCounts);
        setIsLoading(false);
      }, 500);
    };

    // Initial load
    updateCounts();

    // Update every 30 seconds to simulate real-time updates
    const interval = setInterval(updateCounts, 30000);

    return () => clearInterval(interval);
  }, []);

  return { counts, isLoading, refreshCounts: () => setCounts({ ...counts }) };
};

/**
 * Get resource count for specific provider
 */
export const getResourceCount = (provider: CloudProvider | 'all', counts: Record<string, number>): number => {
  return counts[provider] || 0;
};

/**
 * Calculate filtered resource count based on provider selection
 */
export const calculateFilteredResourceCount = (
  globalProvider: CloudProvider,
  localFilter: string,
  allResources: any[]
): number => {
  let filtered = allResources;

  // Apply global provider filter
  if (globalProvider !== 'all' as any) {
    filtered = filtered.filter(resource => resource.provider === globalProvider);
  }

  // Apply local filter if specified
  if (localFilter && localFilter !== 'all') {
    filtered = filtered.filter(resource => resource.provider === localFilter);
  }

  return filtered.length;
};